/**
 * Register API data source — pure rules for the wizard (AVA-602, showcase D / plan N29).
 *
 * Mirrors the server (aiva.api RegisterApiSourceConfigurationPolicy):
 *   - a non-Manual schedule needs a non-blank identity column (SCHEDULED_SOURCE_IDENTITY_REQUIRED);
 *   - a populated Facility Group register without a Shared/Separated mode can only be saved as
 *     Manual Only, without an immediate first run (the refusal carries no code).
 * The wizard surfaces these rules before Save; the server stays the authority.
 */
import { REGISTER_KIND, isBlankCell } from './registerImportWizardModel.js';

/** ScheduleConfigDto.frequency (aiva.api ScheduleFrequency). */
export const FREQUENCY = Object.freeze({ MANUAL: 0, HOURLY: 1, DAILY: 2, WEEKLY: 3, CUSTOM: 4 });

const FREQUENCY_LABELS = Object.freeze({
  [FREQUENCY.MANUAL]: 'Manual Only',
  [FREQUENCY.HOURLY]: 'Hourly',
  [FREQUENCY.DAILY]: 'Daily',
  [FREQUENCY.WEEKLY]: 'Weekly',
  [FREQUENCY.CUSTOM]: 'Custom (Cron)',
});

/** Wizard steps (1-based, WizardShell order). */
export const API_STEP = Object.freeze({ CONFIGURE: 1, TEST: 2, MAP: 3, SCHEDULE: 4, REVIEW: 5 });

/** Server refusal code for a scheduled source without an identity column. */
export const SCHEDULED_IDENTITY_REQUIRED_CODE = 'SCHEDULED_SOURCE_IDENTITY_REQUIRED';

const fmt = (value) => Number(value || 0).toLocaleString('en-US');

export function frequencyLabel(frequency) {
  return FREQUENCY_LABELS[frequency] || FREQUENCY_LABELS[FREQUENCY.MANUAL];
}

export function isScheduled(frequency) {
  return Number(frequency || 0) !== FREQUENCY.MANUAL;
}

/** Server parity: any non-blank identity column counts (RequireMappedIdentity). */
export function hasIdentityColumn(mapping) {
  return !!(mapping && typeof mapping.identityColumn === 'string' && mapping.identityColumn.trim());
}

export function mappedFieldCount(mapping) {
  const fields = (mapping && mapping.fieldMappings) || {};
  return Object.keys(fields).filter((column) => fields[column] && String(fields[column]).trim()).length;
}

/** Display name of the identity column: its register field name, else the source column. */
export function identityLabel(mapping) {
  if (!hasIdentityColumn(mapping)) return '';
  const column = mapping.identityColumn;
  const field = mapping.fieldMappings && mapping.fieldMappings[column];
  return field && String(field).trim() ? field : column;
}

/**
 * The identity column to offer ("Use X"): the first CONFIGURED column (in column order) whose
 * value is non-blank and unique in every preview row. Only configured columns qualify — the
 * identity radio exists only for them, and using an unmapped column would invent a field name.
 * @param {Array<object>} rows - preview rows
 * @param {Array<string>} columns - configured source columns, in table order
 * @param {Array<string>} [exclude] - columns that can't be the identity (e.g. the Facility column)
 * @returns {string} column, or '' when none qualifies
 */
export function suggestIdentityColumn(rows, columns, exclude = []) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return '';
  const skip = new Set((exclude || []).filter(Boolean));
  const found = (columns || []).find((column) => {
    if (!column || skip.has(column)) return false;
    const seen = new Set();
    return list.every((row) => {
      const value = row ? row[column] : undefined;
      if (isBlankCell(value) || typeof value === 'object') return false;
      const key = String(value).trim();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  });
  return found || '';
}

/**
 * Identity guidance on Map Fields for an API source (the Excel import keeps its generic warning).
 * @param {object} state - { frequency, registerKind, configured, hasIdentity, identityLabel,
 *   identityColumn, suggestion }
 * @returns {?object} alert { key, kind, icon, title, text, role?, actions? }
 */
export function apiIdentityAlert(state) {
  if (!state || !state.configured) return null;
  const unset = state.registerKind === REGISTER_KIND.GROUP_UNSET;
  const scheduled = isScheduled(state.frequency);
  const label = frequencyLabel(state.frequency);
  const useAction = state.suggestion
    ? { key: 'use-identity', label: `Use ${state.suggestion}`, icon: 'mdi-fingerprint', outlined: true }
    : null;

  if (state.hasIdentity) {
    if (unset) return null;
    return {
      key: 'identity',
      kind: 'ok',
      icon: 'mdi-fingerprint',
      title: `Identity: ${state.identityLabel || state.identityColumn}.`,
      text: `Re-runs update the row with the same “${state.identityColumn}”${scheduled ? `; ${label.toLowerCase()} schedule allowed.` : '.'}`,
    };
  }

  if (scheduled && !unset) {
    return {
      key: 'identity',
      kind: 'warn',
      role: 'status',
      icon: 'mdi-fingerprint-off',
      title: `${label} schedule needs an identity column.`,
      text: `Without one, every pull adds all rows again, so the schedule can’t be saved.${state.suggestion ? ` “${state.suggestion}” has a unique value in every preview row.` : ' Pick a column with a unique value in every row.'}`,
      actions: [useAction, { key: 'switch-manual', label: 'Switch to Manual Only' }].filter(Boolean),
    };
  }

  return {
    key: 'identity',
    kind: 'info',
    icon: 'mdi-fingerprint-off',
    title: 'No identity column.',
    text: 'Each manual pull adds every row again instead of updating it. Scheduled pulls need one.',
    actions: useAction ? [useAction] : null,
  };
}

/** Why the non-Manual schedules are locked on the Schedule step (empty = none are locked). */
export function scheduleBlockers({ hasIdentity, registerKind, registerName, registerRowCount }) {
  const list = [];
  if (!hasIdentity) {
    list.push({
      key: 'identity',
      short: 'Needs an identity column',
      title: 'No identity column.',
      text: 'Scheduled pulls must match rows on an identity column, or every pull adds all rows again.',
      goStep: API_STEP.MAP,
      goLabel: 'Back to Map Fields to pick one',
    });
  }
  if (registerKind === REGISTER_KIND.GROUP_UNSET) {
    list.push({
      key: 'unset',
      short: 'Needs the register mode',
      title: 'Register mode not set.',
      text: `${registerName || 'This register'} has ${fmt(registerRowCount)} rows and no Shared or Separated mode. Until an administrator sets it, only Manual Only can be saved, without an immediate first run.`,
      goStep: null,
      goLabel: '',
    });
  }
  return list;
}

/** Rules still broken at Review; Save stays disabled until the list is empty. */
export function saveBlockers({ frequency, triggerImmediate, hasIdentity, registerKind, fieldCount }) {
  const list = [];
  if (!fieldCount) {
    list.push({
      key: 'fields',
      title: 'No register fields mapped.',
      text: 'Name at least one register field for the API columns.',
      goStep: API_STEP.MAP,
      goLabel: 'Fix in Map Fields',
    });
  }
  if (isScheduled(frequency) && !hasIdentity) {
    list.push({
      key: 'identity',
      title: `${frequencyLabel(frequency)} schedule needs an identity column.`,
      text: 'Pick one in Map Fields, or switch the schedule to Manual Only.',
      goStep: API_STEP.MAP,
      goLabel: 'Fix in Map Fields',
    });
  }
  if (registerKind === REGISTER_KIND.GROUP_UNSET && (isScheduled(frequency) || triggerImmediate)) {
    list.push({
      key: 'unset',
      title: 'Register mode not set.',
      text: 'Only Manual Only, without an immediate first run, can be saved until an administrator sets the mode.',
      goStep: API_STEP.SCHEDULE,
      goLabel: 'Fix in Schedule',
    });
  }
  return list;
}

/**
 * The inline Review message for a refused save. Keyed on the server's `code` only — never on the
 * message text; an uncoded refusal shows its message verbatim.
 * @param {?object} data - err.response.data ({ code?, message?, detail? }) or null
 * @param {string} [fallback] - when the response carries no message (network error, etc.)
 */
export function saveErrorPresentation(data, fallback = '') {
  const body = data && typeof data === 'object' ? data : {};
  const message = (typeof body.message === 'string' && body.message.trim()) || fallback || 'The server did not say why. Please try again.';
  if (body.code === SCHEDULED_IDENTITY_REQUIRED_CODE) {
    return {
      title: 'A scheduled source needs an identity column.',
      text: message,
      goStep: API_STEP.MAP,
      goLabel: 'Fix in Map Fields',
    };
  }
  return { title: 'The source wasn’t saved.', text: message, goStep: null, goLabel: '' };
}
